/**
 * A post's words, drawn the way the post itself says they should be.
 *
 * Every surface that shows a post — the feed, a profile, a classroom's list, the
 * thread page, a bookmark — draws the words through this one component, so *a
 * styled post is styled wherever it is read* rather than only in the place it was
 * first supported. That is the same rule the capsule and book cards already
 * follow, and it is why this decision lives here and not in five callers.
 *
 * The decision is one record read: a post whose record carries the stylizer mark
 * is drawn as a design (`PostDesignRenderer`), and every other post is drawn exactly as
 * it was before — the same `RichText`, the same facets, the same "show more" when
 * a long post is clamped. A post written by another client, or by a build that has
 * never heard of styling, therefore goes down the second path and looks untouched.
 */
import {useMemo} from 'react'
import {type StyleProp, type TextStyle} from 'react-native'
import {
  AppBskyEmbedImages,
  type AppBskyFeedDefs,
  type RichText,
} from '@atproto/api'

import {ShowMoreTextButton} from '#/components/Post/ShowMoreTextButton'
import {RichText as RichTextComponent} from '#/components/RichText'
import {PostDesignRenderer} from '#/components/Post/PostDesignRenderer'
import {styledBackgroundImageUrl} from '#/state/postStyle/image'
import {readStyledPost} from '#/state/postStyle/types'

export function PostBody({
  post,
  value,
  textStyle,
  numberOfLines,
  enableTags,
  selectable,
  shouldProxyLinks,
  authorHandle,
  testID,
  onPressShowMore,
}: {
  post: AppBskyFeedDefs.PostView
  value: RichText
  textStyle: StyleProp<TextStyle>
  numberOfLines?: number
  enableTags?: boolean
  selectable?: boolean
  shouldProxyLinks?: boolean
  authorHandle?: string
  testID?: string
  /** Only the clamped, unstyled path ever needs this. */
  onPressShowMore?: () => void
}) {
  /*
   * The mark is read from the post's own record and validated by its own schema,
   * so a surface that reached this post some other way — a shared link, a
   * message, a bookmark — reads it here rather than having to have validated the
   * record first. A malformed mark reads as no mark at all (`readStyledPost`).
   */
  const styled = useMemo(() => readStyledPost(post), [post])
  const text =
    typeof (post.record as {text?: unknown} | undefined)?.text === 'string'
      ? ((post.record as {text: string}).text ?? '')
      : ''

  if (styled && text) {
    return (
      <PostDesignRenderer
        style={styled}
        text={text}
        /*
         * The design's picture is the post's own when the mark names no blob — the
         * very image this post carries, addressed by the embed the reader already
         * has — and a blob in the author's repo when it does, fetched from the image
         * host this app is already talking to, with the addressing borrowed from a
         * URL it has already been given (see `styledBackgroundImageUrl`). Without
         * either, the card keeps the colour the author chose.
         */
        backgroundImageUri={styledBackgroundImageUrl(styled, {
          avatarUrl: post.author.avatar,
          postImageUrl: postImageUrlOf(post),
        })}
      />
    )
  }

  return (
    <>
      <RichTextComponent
        testID={testID}
        value={value}
        numberOfLines={numberOfLines}
        enableTags={enableTags}
        selectable={selectable}
        style={textStyle}
        authorHandle={authorHandle}
        shouldProxyLinks={shouldProxyLinks}
      />
      {numberOfLines && onPressShowMore ? (
        <ShowMoreTextButton style={textStyle} onPress={onPressShowMore} />
      ) : null}
    </>
  )
}

/**
 * The post's own picture, as the image host addresses it.
 *
 * A design's background *is* this picture when its mark names no blob, so the
 * address is taken from the embed the reader already has rather than built from
 * anything: the full-size preset, because the design draws it across the whole
 * composition. A post whose picture is a video, a link or a quote answers
 * `undefined`, and the design keeps its colour.
 */
function postImageUrlOf(post: AppBskyFeedDefs.PostView): string | undefined {
  const embed = post.embed
  if (!embed || !AppBskyEmbedImages.isView(embed)) return undefined
  return embed.images[0]?.fullsize
}
